import { addDishToCart } from '../cart'
import { syncGuestFavourites } from '../favourites'
import type { UiDish } from '../projections'

export interface PendingAction {
  type: 'add_to_cart' | 'plan_meal' | 'checkout'
  dish?: {
    id: string
    name: string
    price: number
    chefId: string
    chef?: string
    category?: string
    image?: string
    glyph?: string
  }
  quantity?: number
  dayKey?: string
  returnTo?: string
  timestamp: number
}

const STORAGE_KEY = 'chow_pending_action'

export const recordPendingAction = (action: PendingAction): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(action))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(action))
  } catch {
    /* storage unavailable */
  }
}

export const getPendingAction = (): PendingAction | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const action = JSON.parse(raw) as PendingAction
    // Actions older than 24 hours expire
    if (Date.now() - action.timestamp > 24 * 60 * 60 * 1000) {
      clearPendingAction()
      return null
    }
    return action
  } catch {
    return null
  }
}

export const clearPendingAction = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* storage unavailable */
  }
}

/**
 * Execute any recorded action after successful authentication.
 * Returns the destination path to navigate to, if any.
 */
export const executePendingAction = async (profileId: string): Promise<string | null> => {
  await syncGuestFavourites(profileId)
  const pending = getPendingAction()
  if (!pending) return null

  try {
    if (pending.type === 'add_to_cart' && pending.dish) {
      const uiDish: UiDish = {
        id: pending.dish.id,
        name: pending.dish.name,
        price: pending.dish.price,
        chefId: pending.dish.chefId,
        chef: pending.dish.chef || 'ChooseChow Kitchen',
        category: (pending.dish.category as UiDish['category']) || 'all',
        chefGlyph: '👩🏾‍🍳',
        description: '',
        status: 'available',
        availableDays: [],
        ingredients: [],
        allergens: [],
        cookTimeMinutes: 20,
        menuId: '',
        chefBadge: '',
        image: pending.dish.image,
        glyph: pending.dish.glyph || '🍽️',
      }
      await addDishToCart(uiDish, pending.quantity || 1, profileId)
    }
    const dest = pending.returnTo || null
    clearPendingAction()
    return dest
  } catch (err) {
    console.error('Failed to execute pending action:', err)
    clearPendingAction()
    return null
  }
}
